import { useCallback, useEffect, useRef, useState } from "react"
import { supabase } from "../supabaseClient"
import { useUser } from "../UserContext"
import { useOrganization } from "../OrganizationContext"
import { useLocation, useNavigate } from "react-router-dom"
import "../App.css"
import { v4 as uuidv4 } from "uuid"
import { normalizeUrl } from "../utils/security"
import { Button } from "../components/ui"
import { getValidSupabaseAccessToken } from "../utils/supabaseSession"


export default function WebsiteScraping() {
  const user = useUser()
  const { selectedOrgId } = useOrganization()
  const navigate = useNavigate()
  const location = useLocation()
  // Stores the textarea value where users can enter one or more URLs
  const [urlsText, setUrlsText] = useState("")
  const [submittedUrls, setSubmittedUrls] = useState([])
  const [selectedUrls, setSelectedUrls] = useState([])
  const toggleUrlSelection = (urlId) => {
    setSelectedUrls((prev) =>
      prev.includes(urlId)
        ? prev.filter((id) => id !== urlId)
        : [...prev, urlId]
    )
  }
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [gtmSummaryMap, setGtmSummaryMap] = useState({})
  const [ga4SummaryMap, setGa4SummaryMap] = useState({})
  const [legacySummaryMap, setLegacySummaryMap] = useState({})
  const selectAllRef = useRef(null)
  const allSelected =
    submittedUrls.length > 0 &&
    submittedUrls.every((entry) => selectedUrls.includes(entry.id))
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedUrls([])
    } else {
      setSelectedUrls(submittedUrls.map((entry) => entry.id))
    }
  }

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate =
        selectedUrls.length > 0 && !allSelected
    }
  }, [selectedUrls, allSelected])

  const fetchGtmSummaries = useCallback(async () => {
    if (!user) return

    const { data, error } = await supabase.rpc("get_gtm_summaries", {
      uid: user.id,
      org_id: selectedOrgId,
    })

    if (!error && data) {
      const map = {}
      data.forEach((row) => {
        map[row.website_id] = row
      })
      setGtmSummaryMap(map)
    }
  }, [user, selectedOrgId])

  const fetchGa4Summaries = useCallback(async () => {
    if (!user) return

    const { data } = await supabase.rpc("get_ga4_summaries", {
      uid: user.id,
      org_id: selectedOrgId,
    })
    if (data) {
      const map = {}
      data.forEach((row) => { map[row.website_id] = row })
      setGa4SummaryMap(map)
    }
  }, [user, selectedOrgId])

  const fetchLegacySummaries = useCallback(async () => {
    if (!user) return

    const { data } = await supabase.rpc("get_legacy_code_summaries", {
      uid: user.id,
      org_id: selectedOrgId,
    })
    if (data) {
      const map = {}
      data.forEach((row) => { map[row.website_id] = row })
      setLegacySummaryMap(map)
    }
  }, [user, selectedOrgId])

  useEffect(() => {
    if (!user) return

    const loadUrls = async () => {
      let query = supabase.from("website_urls").select("*")
      if (selectedOrgId) query = query.eq("organization_id", selectedOrgId)
      else query = query.eq("user_id", user.id)
      const { data, error } = await query.order("created_at", { ascending: false })

      if (error) console.error("Fetch error:", error)
      else setSubmittedUrls(data || [])
    }

    loadUrls()
    fetchGtmSummaries()
    fetchGa4Summaries()
    fetchLegacySummaries()
  }, [
    user,
    selectedOrgId,
    fetchGtmSummaries,
    fetchGa4Summaries,
    fetchLegacySummaries,
  ])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrorMessage(null)
    if (!urlsText.trim()) return

    const lines = urlsText
      .split(/\n+/)
      .map((l) => l.trim())
      .filter((l) => l)

    if (lines.length === 0) return

    const existingNormalized = submittedUrls
      .map((u) => normalizeUrl(u.url))
      .filter(Boolean)

    const seen = new Set()
    const entries = []

    for (const line of lines) {
      const normalized = normalizeUrl(line)
      if (!normalized) continue

      const alreadyExists = existingNormalized.includes(normalized)
      if (!alreadyExists && !seen.has(normalized)) {
        seen.add(normalized)
        entries.push(
          selectedOrgId
            ? { id: uuidv4(), organization_id: selectedOrgId, url: normalized }
            : { id: uuidv4(), user_id: user.id, url: normalized }
        )
      }
    }

    if (entries.length === 0) {
      setErrorMessage("All URLs have already been submitted.")
      return
    }

    const { error } = await supabase.from("website_urls").insert(entries)

    if (!error) {
      setUrlsText("")
      let refresh = supabase.from("website_urls").select("*")
      if (selectedOrgId) refresh = refresh.eq("organization_id", selectedOrgId)
      else refresh = refresh.eq("user_id", user.id)
      const { data, error: fetchError } = await refresh.order("created_at", { ascending: false })

      if (!fetchError) setSubmittedUrls(data || [])
      fetchGtmSummaries()
    } else {
      console.error("Insert error:", error)
      setErrorMessage("Failed to submit URL(s).")
    }
  }

  const runScraper = async () => {
    setLoading(true)
    setErrorMessage(null)

    const token = await getValidSupabaseAccessToken(supabase)
    if (!token) {
      console.error("Missing access token")
      setErrorMessage("Authentication error. Please re-sign in.")
      setLoading(false)
      return
    }

    for (const urlEntry of submittedUrls.filter((u) => selectedUrls.includes(u.id))) {
      try {
        const body = {
          url: urlEntry.url,
          website_id: urlEntry.id,
          organization_id: selectedOrgId || null,
        }

        const scrapeRes = await fetch(
          "https://agavfphsguwtoagpvekp.functions.supabase.co/scrape-all",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(body),
          },
        )

        const text = await scrapeRes.text()
        if (!scrapeRes.ok) {
          throw new Error(text)
        }
      } catch (error) {
        console.error(`Error scraping ${urlEntry.url}:`, error)
        const manualHtml = window.prompt(
          `Failed to scrape ${urlEntry.url}. Paste HTML manually:`,
        )
        if (manualHtml && manualHtml.trim()) {
          await uploadHtmlForUrl(urlEntry, manualHtml.trim())
        }
      }
    }
    setLoading(false)
    fetchGtmSummaries()
    fetchGa4Summaries()
    fetchLegacySummaries()
  }

  const uploadHtmlForUrl = async (urlEntry, html) => {
    const token = await getValidSupabaseAccessToken(supabase)
    if (!token) {
      console.error("Missing access token")
      setErrorMessage("Authentication error. Please re-sign in.")
      return false
    }

    try {
      const body = {
        url: urlEntry.url,
        website_id: urlEntry.id,
        organization_id: selectedOrgId || null,
        html,
      }

      const resp = await fetch(
        "https://agavfphsguwtoagpvekp.functions.supabase.co/scrape-all",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        },
      )
      await resp.text()
      return resp.ok
    } catch (error) {
      console.error("Error uploading HTML:", error)
      return false
    }
  }


  const deleteSelectedUrls = async () => {
    if (selectedUrls.length === 0) return
    if (!window.confirm("Delete selected URLs? This will remove associated data.")) {
      return
    }
    setLoading(true)
    setErrorMessage(null)

    const { error } = await supabase
      .from("website_urls")
      .delete()
      .in("id", selectedUrls)

    if (error) {
      console.error("Delete error:", error)
      setErrorMessage("Failed to delete URL(s).")
    } else {
      setSubmittedUrls((prev) => prev.filter((u) => !selectedUrls.includes(u.id)))
      setSelectedUrls([])
      const gtm = { ...gtmSummaryMap }
      const ga4 = { ...ga4SummaryMap }
      const legacy = { ...legacySummaryMap }
      selectedUrls.forEach((id) => {
        delete gtm[id]
        delete ga4[id]
        delete legacy[id]
      })
      setGtmSummaryMap(gtm)
      setGa4SummaryMap(ga4)
      setLegacySummaryMap(legacy)
    }
    setLoading(false)
  }


  return (
    <div className="page-container page-container--wide">
      <h2>Website Scraping</h2>

      {errorMessage && (
        <div style={{ color: "red", marginBottom: "1rem" }}>{errorMessage}</div>
      )}

      <form onSubmit={handleSubmit}>
        <textarea
          placeholder="Enter website URLs, one per line"
          value={urlsText}
          onChange={(e) => setUrlsText(e.target.value)}
          style={{ width: "300px", height: "100px", marginRight: "0.5rem" }}
        />
        <button type="submit">Submit</button>
      </form>

      {selectedUrls.length > 0 && (
        <div
          className="ui-stack ui-stack--row"
          style={{ marginTop: "1rem", alignItems: "center" }}
        >
          <Button onClick={runScraper} disabled={loading}>
            {loading ? "Scraping..." : "Run Scraper"}
          </Button>
          <Button
            variant="destructive"
            onClick={deleteSelectedUrls}
            disabled={loading}
          >
            Delete Selected
          </Button>
        </div>
      )}

      <div style={{ marginTop: "2rem", overflowX: "auto" }}>
        <div
          style={{
            maxHeight: "60vh",
            overflowY: "auto",
            border: "1px solid var(--border-color, #e2e8f0)",
            borderRadius: "var(--border-radius, 8px)",
            boxShadow: "0 1px 2px rgba(15, 23, 42, 0.05)",
          }}
        >
          <table className="ui-table"
            style={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: "880px",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    position: "sticky",
                    top: 0,
                    backgroundColor: "#1e3a8a",
                    color: "white",
                    zIndex: 2,
                    textAlign: "center",
                    padding: "0.5rem 0.75rem",
                    borderBottom: "1px solid var(--border-color, #e2e8f0)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <input
                      type="checkbox"
                      ref={selectAllRef}
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      disabled={submittedUrls.length === 0}
                      aria-label="Select all websites"
                    />
                  </div>
                </th>
                {["Website URL", "Last Scraped Date", "GTM's on Site", "GTM Source", "GA4 on Site", "GA4 Source", "Legacy Code", "View Details"].map((header) => (
                  <th
                    key={header}
                    style={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#1e3a8a",
                      color: "white",
                      zIndex: 2,
                      textAlign: "left",
                      padding: "0.5rem 0.75rem",
                      borderBottom: "1px solid var(--border-color, #e2e8f0)",
                      whiteSpace: header === "View Details" ? "nowrap" : "normal",
                    }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {submittedUrls.map((entry) => {
                const gtmSummary = gtmSummaryMap[entry.id] || {}
                const ga4Summary = ga4SummaryMap[entry.id] || {}
                const legacySummary = legacySummaryMap[entry.id] || {}
                const isSelected = selectedUrls.includes(entry.id)
                const sharedCellStyle = {
                  padding: "0.5rem 0.75rem",
                  borderBottom: "1px solid var(--border-color, #e2e8f0)",
                  backgroundColor: isSelected
                    ? "rgba(30, 64, 175, 0.08)"
                    : "transparent",
                  color: "inherit",
                }
                return (
                  <tr
                    key={entry.id}
                    style={{
                      backgroundColor: isSelected
                        ? "rgba(30, 64, 175, 0.08)"
                        : "transparent",
                    }}
                  >
                    <td
                      onClick={() => toggleUrlSelection(entry.id)}
                      style={{
                        ...sharedCellStyle,
                        cursor: "pointer",
                        textAlign: "center",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleUrlSelection(entry.id)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Select ${entry.url}`}
                      />
                    </td>
                    <td style={sharedCellStyle}>{entry.url}</td>
                    <td style={sharedCellStyle}>
                      {gtmSummary.last_scrape_date
                        ? gtmSummary.last_scrape_date.split("T")[0]
                        : "—"}
                    </td>
                    <td style={sharedCellStyle}>{gtmSummary.active_gtm_count ?? 0}</td>
                    <td style={sharedCellStyle}>{gtmSummary.last_found_method || "—"}</td>
                    <td style={sharedCellStyle}>{ga4Summary.active_ga4_count ?? 0}</td>
                    <td style={sharedCellStyle}>{ga4Summary.last_found_method || "—"}</td>
                    <td style={sharedCellStyle}>{legacySummary.active_code_count ?? 0}</td>
                    <td style={{ ...sharedCellStyle, whiteSpace: "nowrap" }}>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          navigate(`/website-audit/details/${entry.id}`, {
                            state: {
                              from: `${location.pathname}${location.search}`,
                            },
                          })
                        }
                      >
                        View Details
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selectedUrls.length > 0 && (
        <div
          className="ui-stack ui-stack--row"
          style={{ marginTop: "1rem", alignItems: "center" }}
        >
          <Button onClick={runScraper} disabled={loading}>
            {loading ? "Scraping..." : "Run Scraper"}
          </Button>
          <Button
            variant="destructive"
            onClick={deleteSelectedUrls}
            disabled={loading}
          >
            Delete Selected
          </Button>
        </div>
      )}
    </div>
  )
}
